import React, { useRef, useEffect, useState } from 'react';
import { GoogleMap, DirectionsService, DirectionsRenderer, Marker } from "@react-google-maps/api";
import { useJsApiLoader } from "@react-google-maps/api";

// Default location: Boston Logan International Airport (BOS)
const defaultCenter = {
  lat: 42.3656338,  // Latitude of Boston Logan International Airport
  lng: -71.0282381, // Longitude of Boston Logan International Airport
};

const Map = ({ direction, pickup, dropoff }) => {
  const mapRef = useRef(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  useEffect(() => {
    if (isLoaded && pickup && dropoff) {
      // If both pickup and dropoff are set, center the map between them
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(new window.google.maps.LatLng(pickup.lat, pickup.lng));
      bounds.extend(new window.google.maps.LatLng(dropoff.lat, dropoff.lng));
      mapRef.current?.fitBounds(bounds);
    } else if (pickup) {
      setMapCenter({ lat: pickup.lat, lng: pickup.lng });
    } else if (dropoff) {
      setMapCenter({ lat: dropoff.lat, lng: dropoff.lng });
    } else {
      setMapCenter(defaultCenter);
    }
  }, [pickup, dropoff, isLoaded]);

  const handleMapLoad = (map) => {
    mapRef.current = map;
    map.setZoom(12);
  };

  const containerStyle = {
    width: '100%',
    height: '100%',
  };

  if (!isLoaded) {
    return (
      <div style={{ height: "70vh" }} className="d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading Google Maps...</p>
          {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
            <div className="alert alert-warning mt-3">
              <strong>Warning:</strong> Google Maps API key is not configured. 
              Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file.
            </div>
          ) : (
            <div className="alert alert-danger mt-3">
              <strong>API Error:</strong> Google Maps JavaScript API is not enabled.
              <br />
              <small>
                Go to <a href="https://console.cloud.google.com/google/maps-apis/api/maps-backend.googleapis.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> and enable the Maps JavaScript API.
              </small>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <GoogleMap 
      mapContainerStyle={containerStyle} 
      center={mapCenter}
      zoom={12}
      onLoad={handleMapLoad}
    >
      {/* Markers for Pickup and Dropoff */}
      {pickup && (
        <Marker 
          position={pickup} 
          title="Pickup"
          label={{text: "P", color: "white", fontWeight: "bold"}}
          icon={{
            path: "M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z",
            fillColor: "#4285F4",
            fillOpacity: 1,
            strokeWeight: 1,
            scale: 1
          }}
        />
      )}
      {dropoff && (
        <Marker 
          position={dropoff} 
          title="Dropoff"
          label={{text: "D", color: "white", fontWeight: "bold"}}
          icon={{
            path: "M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z",
            fillColor: "#EA4335",
            fillOpacity: 1,
            strokeWeight: 1,
            scale: 1
          }}
        />
      )}

      {/* Directions Renderer */}
      {direction && <DirectionsRenderer directions={direction} />}
    </GoogleMap>
  );
};

export default Map;
